import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { utils, Button } from '@makeship/core';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';

import posthog from 'posthog-js';
import Drawer from '../Drawer';
import { Label, Paragraph } from '../../ui/typography';
import { NavLink } from '../../ui/atoms/Link/NavLink';
import CartDropdown from '../CartDropdown';

import { useStore } from '../../store';
import { toggleCart } from '../../store/store.actions';
import { getNumItems } from '../../utils/cart';
import { useDebounce } from '../../utils/debounce';
import useWindowOffset from '../../utils/window';
import { SearchBar } from '../Input';
import { getProductsByQuery } from '../../api/product';
import { findCollectionByMetafield, getProductIDFromShopifyGID, getShopifyQuery, getStage } from '../../utils/product';

import config from '../../../config.json';
import {
  getCollectionByHandle,
  getCreatorCollectionProductsByQuery,
  getCreatorCollectionsByQuery,
  getSearchableCollectionsByQuery,
} from '../../api/collections';
import { CartEventLocation, NavTheme, ProductStage } from '../../types/common';
import {
  getSortedCreatorSearchResults,
  getSortedProductSearchResults,
  parseCreatorCollectionMetafields,
} from '../../utils/collections';
import { analyticsTrackViewCart, getCreator, isUserLoggedIn } from '../../utils/analytics';
import { Avatar } from '../../ui/atoms/Avatar';

const { collections, routes } = config;

const getBackground = (navTheme: NavTheme, theme: DefaultTheme) => {
  switch (navTheme) {
    case NavTheme.Dark:
      return theme.colors.neutral7;
    case NavTheme.Light:
      return theme.colors.neutral1;
    default:
      return theme.colors.neutral1;
  }
};

const getNavItemColor = (navTheme: NavTheme | undefined): 'primary-black' | 'primary-white' => {
  switch (navTheme) {
    case NavTheme.Dark:
      return 'primary-white';
    case NavTheme.Light:
    default:
      return 'primary-black';
  }
};

const NavContainer = styled.div<{ offset: number }>`
  margin-bottom: ${({ offset, theme }) => offset > theme.bannerHeight && `${theme.headerHeight}px`};
`;

const HeaderWrapper = styled.div<{ offset: number; navTheme: string | undefined }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: ${({ theme }) => theme.siteWidth};
  height: ${({ theme }) => `${theme.headerHeight}px`};
  background: ${({ navTheme, theme }) => getBackground((navTheme || NavTheme.default) as NavTheme, theme)};
  position: ${({ offset, theme }) => (offset > theme.bannerHeight ? 'fixed' : 'relative')};
  top: 0;
  padding-right: 24px;
  z-index: 3;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    padding-right: 16px;
  }
`;

const HeaderSide = styled.div`
  flex: 1 1;
  display: flex;
  align-items: center;
`;

const LeftHeaderSide = styled(HeaderSide)`
  justify-content: flex-start;
`;

const CenterHeaderSide = styled(HeaderSide)`
  justify-content: center;
  flex: 1 0;
  & > a {
    white-space: nowrap;
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    margin-left: 0;
  }
`;

const RightHeaderSide = styled(HeaderSide)`
  justify-content: flex-end;
  flex-shrink: 0;
`;

const SearchBarWrapper = styled.form`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    display: none;
  }
`;

const SearchResultsCard = styled.div`
  position: absolute;
  width: 400px;
  margin-top: 8px;
  background: ${({ theme }) => theme.colors.neutral1};
  border-radius: 3px;
  box-shadow: 0px 2px 16px ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.medium)};
  display: flex;
  flex-direction: column;
`;

const SearchResultWrapper = styled.div`
  padding: 8px 16px;
  display: flex;
  cursor: pointer;
  border-radius: 3px;
  &:hover {
    background-color: ${({ theme }) => theme.colors.neutral2};
  }
  align-items: center;
  gap: 12px;
`;

const SearchForm = styled.form`
  width: 100%;
`;

const SearchResultImage = styled.div`
  width: 72px;
  min-width: 72px;
  height: 72px;
  display: flex;
  justify-content: center;
`;

const SearchResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SearchResultsPriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const SearchBarDropDown = styled.div<{ offset: number; showMenu: boolean }>`
  display: none;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    transform: ${({ showMenu }) => (showMenu ? 'translate(0)' : 'translate(100%)')};
    transition: transform 0.25s ease-in-out;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    position: fixed;
    top: 0;
    right: 0;
    height: 100%;
    width: calc(100vw - 16px);
    max-width: 450px;
    background-color: ${({ theme }) => theme.colors.neutral1};
    box-shadow: 0px 2px 16px ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.medium)};
    z-index: 11;
  }
`;

const SearchBarDropDownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 16px 0 16px;
`;

const SearchBarDropDownStyles = css`
  background-color: ${({ theme }) => theme.colors.neutral2};
  height: 40px;
  width: 90%;
`;

const SearchBarDropDownResults = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
`;

const SearchBarViewAll = styled(Button.PrimaryLink)`
  padding: 16px;
  align-self: flex-end;
`;

const CancelIconWrapper = styled.div`
  cursor: pointer;
  margin-top: 8px;
`;

const MenuWrapper = styled.div`
  display: none;
  cursor: pointer;
  min-width: 32px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    display: flex;
    justify-content: center;
  }
`;

const MagnifyingGlassWrapper = styled.div`
  display: none;
  margin-right: 16px;
  cursor: pointer;
  min-width: 24px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    display: flex;
  }
`;

const Logo = styled.a`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 8px 18px 8px 8px;
  min-width: 40px;
`;

const CartWrapper = styled.div`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  min-width: 32px;
`;

const CartIconWrapper = styled.div`
  width: 24px;
`;

const NewText = styled.span`
  color: ${({ theme }) => theme.colors.secondary};
`;

type NavItem = {
  title: string;
  href: string;
  id?: string;
  isNew?: boolean;
};

const navItems: NavItem[] = [
  {
    title: 'Featured Creators',
    href: routes.featuredCreators,
    id: 'explore-featured-creators-cta',
    isNew: false,
  },
  {
    title: 'Explore Campaigns',
    href: routes.shopLive,
    id: 'explore-campaigns-cta',
    isNew: false,
  },
  {
    title: 'Explore Petitions',
    href: routes.shopPetitions,
    id: 'explore-petitions-cta',
    isNew: false,
  },
];

export type HeaderProps = {
  navTheme: NavTheme | undefined;
};

type SearchSuggestionsType = {
  productSuggestions: Shopify.ProductEdge[];
  creatorCollectionSuggestions: Shopify.CollectionEdge[];
};

export enum ResultType {
  Product = 'product',
  Creator = 'creator',
}

const Header: React.FC<HeaderProps> = ({ navTheme }: HeaderProps) => {
  const { state, dispatch } = useStore();
  const { pagination } = config;
  const [searchQuery, setSearchQuery] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [showSearchDropDown, setShowSearchDropDown] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [productSearchResults, setProductSearchResults] = useState<Shopify.ProductEdge[]>([]);
  const [creatorCollectionSearchResults, setCreatorCollectionSearchResults] = useState<Shopify.CollectionEdge[]>([]);
  const [suggestions, setSuggestions] = useState<SearchSuggestionsType>({
    productSuggestions: [],
    creatorCollectionSuggestions: [],
  });
  const [hasFetchedSuggestions, setHasFetchedSuggestions] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  const offset = useWindowOffset();
  const cartRef = useRef<HTMLDivElement | null>(null);
  const resultsRef = useRef<HTMLFormElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const childInputRef = useRef<HTMLInputElement | null>(null);
  const router = useRouter();
  const debouncedSearchQuery = useDebounce<string>(searchQuery.trim(), 400);

  const handleClick = useCallback((e: MouseEvent) => {
    // outside dropdown div
    if (
      !!searchRef.current &&
      !searchRef.current.contains(e.target as Node) &&
      !!resultsRef.current &&
      !resultsRef.current.contains(e.target as Node)
    ) {
      clear();
    }
  }, []);

  useEffect(() => {
    // add when mounted
    document.addEventListener('mousedown', handleClick);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [handleClick]);

  useEffect(() => {
    debouncedSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleFetchSuggestions = () => {
    if (!hasFetchedSuggestions) {
      getCollectionByHandle(collections.topCampaigns, undefined, 1, pagination.defaultSearchSuggestions).then(
        (data) => {
          const products = data?.products.edges || [];
          const creatorCollection = findCollectionByMetafield(products[0]?.node, 'isCreatorCollection', 'true');
          const collectionArray = creatorCollection ? [creatorCollection] : [];

          setSuggestions({
            productSuggestions: products,
            creatorCollectionSuggestions: collectionArray.slice(0, 1),
          });

          setHasFetchedSuggestions(true);
        },
      );
    }
  };

  const debouncedSearch = async (query: string) => {
    if (!query) {
      setProductSearchResults([]);
      setCreatorCollectionSearchResults([]);
      return;
    }

    const [creatorCollections, productsByTitle, productsByCreator, searchableProducts] = await Promise.all([
      getCreatorCollectionsByQuery(getShopifyQuery(query), 20, '', 'UPDATED_AT', true),
      getProductsByQuery(getShopifyQuery(query), 20, '', 'UPDATED_AT', true),
      getCreatorCollectionProductsByQuery(getShopifyQuery(query), 3, '', 'UPDATED_AT', true),
      getSearchableCollectionsByQuery(getShopifyQuery(query), 3, '', 'UPDATED_AT', true),
    ]);

    const uniqueSearchResults = getSortedProductSearchResults(productsByTitle, productsByCreator, searchableProducts);
    const sortedCreatorResults = getSortedCreatorSearchResults(creatorCollections);

    setProductSearchResults(uniqueSearchResults.slice(0, 3));
    setCreatorCollectionSearchResults(sortedCreatorResults.slice(0, 1));
  };

  const clear = () => {
    setShowSearchDropDown(false);
    setShowSearchResults(false);
  };

  const handleSearchResultClick = (
    searchQuery: string,
    resultType: string,
    product?: Shopify.Product,
    creator?: Shopify.Collection,
  ) => {
    posthog.capture('search_result_click', {
      product_title: product?.title,
      product_id: getProductIDFromShopifyGID(product?.id || ''),
      search_query: searchQuery.trim(),
      location: 'search_bar_suggestion',
      is_logged_in: isUserLoggedIn(state.user),
      product_stage: product && getStage(product?.tags),
      result_type: resultType,
      creator_title: creator?.title,
    });
    clear();
    setSearchQuery('');
  };

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    posthog.capture('search_submit', {
      search_query: searchQuery.trim(),
      is_logged_in: isUserLoggedIn(state.user),
    });
    clear();

    router.push({
      pathname: '/search',
      query: {
        q: searchQuery.trim(),
      },
    });
  };

  const handleSearchIconClick = () => {
    handleFetchSuggestions();
    setShowSearchDropDown(true);
    posthog.capture('search_bar_click', {
      is_logged_in: isUserLoggedIn(state.user),
    });
    childInputRef.current?.focus();
  };

  const handleMenuClick = () => {
    setShowMenu(true);
    posthog.capture('header_menu_click', { is_logged_in: isUserLoggedIn(state.user) });
  };

  return (
    <NavContainer offset={offset}>
      <HeaderWrapper offset={offset} navTheme={navTheme}>
        <LeftHeaderSide>
          <Link href={routes.home} passHref legacyBehavior>
            <Logo
              onClick={() => posthog.capture('header_makeshipLogo_click', { is_logged_in: isUserLoggedIn(state.user) })}
            >
              <Image
                src={`/assets/header-logo${navTheme === NavTheme.Dark ? '' : '-black'}.png`}
                width={40}
                height={40}
                quality={90}
                priority
                alt="Makeship Logo"
              />
            </Logo>
          </Link>
          <MenuWrapper data-testid="menu-wrapper" onClick={handleMenuClick}>
            <Image
              data-testid="menu-image"
              src={`/assets/icons/hamburger-icon${navTheme === NavTheme.Dark ? '-white' : ''}.svg`}
              width={32}
              height={18}
              quality={90}
              priority
              alt="Hamburger menu icon"
            />
          </MenuWrapper>
          <SearchBarWrapper
            onSubmit={search}
            ref={resultsRef}
            onClick={() => posthog.capture('search_bar_click', { isUserLoggedIn: isUserLoggedIn(state.user) })}
          >
            <SearchBar
              placeholder="Search Here"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setShowSearchResults(true);
                setSearchQuery(e.target.value);
              }}
              onFocus={() => setShowSearchResults(true)}
              onClick={handleFetchSuggestions}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              navTheme={navTheme}
            />
            {showSearchResults && (
              <SearchResultsCard>
                {searchQuery.length === 0 ||
                  (productSearchResults.length + creatorCollectionSearchResults.length === 0 && (
                    <Label className="pt-4 pl-4" color="neutral-300">
                      Suggested For You
                    </Label>
                  ))}
                {(creatorCollectionSearchResults.length > 0 ||
                  (suggestions.creatorCollectionSuggestions.length > 0 && productSearchResults.length === 0)) && (
                  <div className="mt-[18px] ml-4">
                    <Label size="large">Creators</Label>
                  </div>
                )}
                {(searchQuery.length > 0 && creatorCollectionSearchResults.length > 0
                  ? creatorCollectionSearchResults
                  : productSearchResults.length === 0
                    ? suggestions.creatorCollectionSuggestions
                    : []
                ).map(({ node }) => {
                  const creatorCollectionMetafields = parseCreatorCollectionMetafields(node.metafields);
                  const { creatorLogoURL, creatorAvatarURL } = creatorCollectionMetafields;

                  return (
                    <Link key={node.id} href={`/shop/creator/${node.handle}`} legacyBehavior>
                      <SearchResultWrapper
                        onClick={() => handleSearchResultClick(searchQuery, ResultType.Creator, undefined, node)}
                      >
                        <Avatar src={creatorLogoURL || creatorAvatarURL} fullName={node.title} size="m" />
                        <Label>{node.title}</Label>
                      </SearchResultWrapper>
                    </Link>
                  );
                })}
                {(productSearchResults.length > 0 || suggestions.productSuggestions.length > 0) && (
                  <div className="mt-[18px] ml-4">
                    <Label size="large">Products</Label>
                  </div>
                )}
                {(searchQuery.length > 0 && productSearchResults.length > 0
                  ? productSearchResults
                  : suggestions.productSuggestions
                ).map(({ node }) => {
                  const stage = getStage(node.tags);
                  const creator = getCreator(node);
                  return (
                    <Link key={node.id} href={`/products/${node.handle}`} legacyBehavior>
                      <SearchResultWrapper
                        onClick={() => handleSearchResultClick(searchQuery, ResultType.Product, node)}
                      >
                        <SearchResultImage>
                          <Image
                            src={node.images.edges[0]?.node.transformedSrc || '/assets/default_product_asset.gif'}
                            alt={node.images.edges[0]?.node.altText || 'product image'}
                            width={72}
                            height={72}
                            quality={90}
                            unoptimized
                          />
                        </SearchResultImage>
                        <SearchResultInfo>
                          <Paragraph>{node.title}</Paragraph>
                          {creator && <Paragraph>By {creator}</Paragraph>}
                          {stage !== ProductStage.Live && stage !== ProductStage.Petition && (
                            <SearchResultsPriceWrapper>
                              <Label className={stage !== ProductStage.ComingSoon ? 'text-red-600 uppercase' : ''}>
                                {stage === ProductStage.ComingSoon ? 'Coming Soon' : 'Sold Out'}
                              </Label>
                            </SearchResultsPriceWrapper>
                          )}
                        </SearchResultInfo>
                      </SearchResultWrapper>
                    </Link>
                  );
                })}
                {searchQuery.length > 0 && productSearchResults.length + creatorCollectionSearchResults.length > 0 && (
                  <SearchBarViewAll>View All Results</SearchBarViewAll>
                )}
              </SearchResultsCard>
            )}
          </SearchBarWrapper>
        </LeftHeaderSide>
        <CenterHeaderSide className="ml-[16px] max-[1024px]:ml-0">
          {navItems.map((item) => (
            <div key={item.title} className="mx-4">
              <Paragraph size="large" color={getNavItemColor(navTheme)}>
                <NavLink
                  href={item.href}
                  className="max-[1024px]:hidden"
                  data-testid={item.id}
                  onClick={() =>
                    posthog.capture(`header_navItem_click`, {
                      nav_item_title: item.title,
                      is_logged_in: isUserLoggedIn(state.user),
                    })
                  }
                >
                  <NewText>{item.isNew && 'New! '}</NewText>
                  {item.title}
                </NavLink>
              </Paragraph>
            </div>
          ))}
        </CenterHeaderSide>
        <RightHeaderSide>
          <div className="mx-4 max-[1024px]:hidden">
            <Paragraph size="large" color={getNavItemColor(navTheme)}>
              <NavLink
                href={isHydrated && state.user?.accessToken ? routes.account : routes.login}
                onClick={() =>
                  posthog.capture(`header_${isHydrated && state.user?.accessToken ? 'myAccount' : 'signUp'}_click`, {
                    is_logged_in: isUserLoggedIn(state.user),
                  })
                }
              >
                {isHydrated && state.user?.accessToken ? 'My Account' : 'Sign In/Up'}
              </NavLink>
            </Paragraph>
          </div>
          <MagnifyingGlassWrapper onClick={handleSearchIconClick}>
            <Image
              src={`/assets/icons/search-icon${navTheme === NavTheme.Dark ? '-white' : ''}.svg`}
              width="23"
              height="24"
              quality={90}
              priority
              alt="Search icon"
            />
          </MagnifyingGlassWrapper>
          <CartWrapper
            onClick={() => {
              dispatch(toggleCart());
              analyticsTrackViewCart(
                state.cart.shopifyCart?.lines.edges,
                state.cart.shopifyCart?.cost.subtotalAmount.amount,
                state.user,
                CartEventLocation.CartPopout,
              );
            }}
            ref={cartRef}
          >
            <CartIconWrapper>
              <Image
                src={`/assets/icons/cart-icon${navTheme === NavTheme.Dark ? '' : '-black'}.svg`}
                width={24}
                height={24}
                quality={90}
                priority
                alt="Shopping cart icon"
              />
            </CartIconWrapper>
            <div className="ml-[4px]">
              <Paragraph size="large" color={getNavItemColor(navTheme)}>
                {getNumItems(state.cart.shopifyCart)}
              </Paragraph>
            </div>
          </CartWrapper>
        </RightHeaderSide>
      </HeaderWrapper>
      <CartDropdown cartRef={cartRef} offset={offset} />
      <Drawer showMenu={showMenu} setShowMenu={setShowMenu} />
      <SearchBarDropDown offset={offset} ref={searchRef} showMenu={showSearchDropDown}>
        <SearchForm onSubmit={search}>
          <SearchBarDropDownHeader>
            <SearchBar
              placeholder="Search Here"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchQuery(e.target.value);
              }}
              customStyles={SearchBarDropDownStyles}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              inputRef={childInputRef}
              navTheme={navTheme}
            />
            <CancelIconWrapper onClick={() => setShowSearchDropDown(false)}>
              <Image
                src="/assets/icons/close-icon.svg"
                width="16"
                height="16"
                quality={90}
                alt="Close search menu icon"
              />
            </CancelIconWrapper>
          </SearchBarDropDownHeader>
          <SearchBarDropDownResults>
            {(searchQuery.length === 0 ||
              productSearchResults.length + creatorCollectionSearchResults.length === 0) && (
              <Label className="pt-4 pl-4" color="neutral-300">
                Suggested For You
              </Label>
            )}
            {(creatorCollectionSearchResults.length > 0 ||
              (suggestions.creatorCollectionSuggestions.length > 0 && productSearchResults.length === 0)) && (
              <div className="mt-[18px] ml-4">
                <Label size="large">Creators</Label>
              </div>
            )}
            {(searchQuery.length > 0 && creatorCollectionSearchResults.length > 0
              ? creatorCollectionSearchResults
              : productSearchResults.length === 0
                ? suggestions.creatorCollectionSuggestions
                : []
            ).map(({ node }) => {
              const creatorCollectionMetafields = parseCreatorCollectionMetafields(node.metafields);
              const { creatorLogoURL, creatorAvatarURL } = creatorCollectionMetafields;

              return (
                <Link key={node.id} href={`/shop/creator/${node.handle}`} legacyBehavior>
                  <SearchResultWrapper
                    onClick={() => handleSearchResultClick(searchQuery, ResultType.Creator, undefined, node)}
                  >
                    <Avatar src={creatorLogoURL || creatorAvatarURL} fullName={node.title} size="m" />
                    <Label>{node.title}</Label>
                  </SearchResultWrapper>
                </Link>
              );
            })}
            {(creatorCollectionSearchResults.length > 0 || suggestions.creatorCollectionSuggestions.length > 0) && (
              <div className="mt-[18px] ml-4">
                <Label size="large">Products</Label>
              </div>
            )}
            {(searchQuery.length > 0 && productSearchResults.length > 0
              ? productSearchResults
              : suggestions.productSuggestions
            ).map(({ node }) => {
              const stage = getStage(node.tags);
              const creator = getCreator(node);
              return (
                <Link key={node.id} href={`/products/${node.handle}`} legacyBehavior>
                  <SearchResultWrapper onClick={() => handleSearchResultClick(searchQuery, ResultType.Product, node)}>
                    <SearchResultImage>
                      <Image
                        src={node.images.edges[0]?.node.transformedSrc || '/assets/default_product_asset.gif'}
                        alt={node.images.edges[0]?.node.altText || 'product image'}
                        width={72}
                        height={72}
                        quality={90}
                        unoptimized
                      />
                    </SearchResultImage>
                    <SearchResultInfo>
                      <Paragraph>{node.title}</Paragraph>
                      {creator && <Paragraph>By {creator}</Paragraph>}
                      {stage !== ProductStage.Live && stage !== ProductStage.Petition && (
                        <SearchResultsPriceWrapper>
                          <Label className={stage !== ProductStage.ComingSoon ? 'text-red-600 uppercase' : ''}>
                            {stage === ProductStage.ComingSoon ? 'Coming Soon' : 'Sold Out'}
                          </Label>
                        </SearchResultsPriceWrapper>
                      )}
                    </SearchResultInfo>
                  </SearchResultWrapper>
                </Link>
              );
            })}
            {searchQuery.length > 0 && productSearchResults.length + creatorCollectionSearchResults.length > 0 && (
              <SearchBarViewAll>View All Results</SearchBarViewAll>
            )}
          </SearchBarDropDownResults>
        </SearchForm>
      </SearchBarDropDown>
    </NavContainer>
  );
};

export default Header;
